import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Pagination from 'react-responsive-pagination';
import 'react-responsive-pagination/themes/classic.css';
import AppLayout from '../components/AppLayout';
import ConstructionStatusBulkUpdateModal from '../components/ConstructionStatusBulkUpdateModal';
import api from '../services/api';
import { toast } from 'react-toastify';
import '../styles/Builders.css';
import '../styles/Buttons.css';
import '../styles/DataTable.css';
import '../styles/Contacts.css';

interface Project {
  _id: string;
  name: string;
}

interface Unit {
  _id: string;
  unitNo: string;
  buildingType?: string;
  status?: string;
  constructionStatus?: string;
  underConstructionDate?: string;
  releasedOnConditionDate?: string;
  releasedDate?: string;
}

interface PaginationData {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

const ConstructionStatus: React.FC = () => {
  const navigate = useNavigate();
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [projects, setProjects] = useState<Project[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [selectedUnits, setSelectedUnits] = useState<Set<string>>(new Set());
  const [showBulkUpdateModal, setShowBulkUpdateModal] = useState(false);
  const isFetchingRef = useRef(false);
  const [pagination, setPagination] = useState<PaginationData>({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  // Filters
  const [filters, setFilters] = useState<{
    projectId?: string;
    buildingType?: string;
    status?: string;
    constructionStatus?: string;
  }>({});

  useEffect(() => {
    if (workspaceId) {
      fetchProjects();
    }
  }, [workspaceId]);

  useEffect(() => {
    if (workspaceId && projects.length > 0 && !filters.projectId) {
      // Set default project filter to first project if not already set
      setFilters({ ...filters, projectId: projects[0]._id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, projects]);

  useEffect(() => {
    if (workspaceId && filters.projectId && !isFetchingRef.current) {
      // Use initial loading for first load, data refresh for subsequent loads
      const isInitialLoad = loading && pagination.page === 1;
      fetchUnits(!isInitialLoad);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.limit, searchQuery, filters]);

  const handleBackToWorkspaces = () => {
    navigate('/workspaces');
  };

  const fetchProjects = async () => {
    if (!workspaceId) return;

    try {
      const response = await api.get(`/workspaces/${workspaceId}/projects`, {
        params: {
          limit: 1000,
          sortBy: 'name',
          sortOrder: 'asc'
        }
      });
      setProjects(response.data.projects);
    } catch (err: any) {
      console.error('Error fetching projects:', err);
      toast.error('Failed to load projects');
    }
  };

  const fetchUnits = async (isDataRefresh: boolean = false) => {
    if (!workspaceId || !filters.projectId) return;

    if (isFetchingRef.current) return;

    try {
      isFetchingRef.current = true;

      if (isDataRefresh) {
        setDataLoading(true);
      } else {
        setLoading(true);
      }

      const params: any = {
        projectId: filters.projectId,
        page: pagination.page,
        limit: pagination.limit,
        sortBy: 'unitNo',
        sortOrder: 'asc'
      };

      if (searchQuery) params.search = searchQuery;
      if (filters.buildingType) params.buildingType = filters.buildingType;
      if (filters.status) params.status = filters.status;
      if (filters.constructionStatus) params.constructionStatus = filters.constructionStatus;

      const response = await api.get(`/workspaces/${workspaceId}/units`, {
        params
      });

      setUnits(response.data.units || []);

      // Update pagination if values changed
      const newPagination = response.data.pagination;
      if (newPagination &&
        (newPagination.page !== pagination.page ||
        newPagination.limit !== pagination.limit ||
        newPagination.total !== pagination.total ||
        newPagination.pages !== pagination.pages)
      ) {
        setPagination(newPagination);
      }

      setSelectedUnits(new Set());
    } catch (err: any) {
      console.error('Error fetching units:', err);
      toast.error('Failed to load units');
    } finally {
      if (isDataRefresh) {
        setDataLoading(false);
      } else {
        setLoading(false);
      }
      isFetchingRef.current = false;
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setPagination({ ...pagination, page: 1 });
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters({ ...filters, [key]: value });
    setPagination({ ...pagination, page: 1 });
  };

  const handlePageChange = (page: number) => {
    setPagination({ ...pagination, page });
  };

  const handleLimitChange = (limit: number) => {
    setPagination({ ...pagination, limit, page: 1 });
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedUnits(new Set(units.map(u => u._id)));
    } else {
      setSelectedUnits(new Set());
    }
  };

  const handleSelectUnit = (unitId: string) => {
    const newSelected = new Set(selectedUnits);
    if (newSelected.has(unitId)) {
      newSelected.delete(unitId);
    } else {
      newSelected.add(unitId);
    }
    setSelectedUnits(newSelected);
  };

  const handleChangeStatus = () => {
    if (selectedUnits.size === 0) {
      toast.error('Please select at least one unit');
      return;
    }
    setShowBulkUpdateModal(true);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setFilters({
      projectId: filters.projectId, // Keep project selection
      buildingType: '',
      status: '',
      constructionStatus: ''
    });
  };

  const handleBulkUpdate = async (data: { constructionStatus: string; dateStatus?: string }) => {
    if (!workspaceId || selectedUnits.size === 0) return;

    try {
      const updateData: any = {
        constructionStatus: data.constructionStatus
      };

      // Set the appropriate date field based on constructionStatus
      if (data.dateStatus) {
        if (data.constructionStatus === 'Under Construction') {
          updateData.underConstructionDate = data.dateStatus;
        } else if (data.constructionStatus === 'Released on Condition') {
          updateData.releasedOnConditionDate = data.dateStatus;
        } else if (data.constructionStatus === 'Released') {
          updateData.releasedDate = data.dateStatus;
        }
      }

      await api.put(`/workspaces/${workspaceId}/units/bulk-update-status`, {
        unitIds: Array.from(selectedUnits),
        updates: updateData
      });

      toast.success(`Successfully updated ${selectedUnits.size} unit(s)`);
      setShowBulkUpdateModal(false);
      setSelectedUnits(new Set());
      fetchUnits(true);
    } catch (err: any) {
      console.error('Error bulk updating:', err);
      toast.error(err.response?.data?.message || 'Failed to update construction status');
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
  };

  const allSelected = units.length > 0 && selectedUnits.size === units.length;
  const selectedUnitNos = units.filter(u => selectedUnits.has(u._id)).map(u => u.unitNo);

  return (
    <AppLayout onBack={handleBackToWorkspaces} backLabel="Workspaces">
      <>
        <div className="builders-container">
          {/* Section Header */}
          <div className="builders-section-header" style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start'
          }}>
            <div>
              <h2 className="builders-section-title">Construction Status</h2>
              <p className="builders-section-subtitle">
                Monitor and update unit construction progress
              </p>
            </div>
            <div style={{ marginTop: '4px' }}>
              <select
                className="form-input"
                value={filters.projectId || ''}
                onChange={(e) => handleFilterChange('projectId', e.target.value)}
                disabled={loading || projects.length === 0}
                style={{ minWidth: '280px' }}
              >
                <option value="">Select Project</option>
                {projects.map((project) => (
                  <option key={project._id} value={project._id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* White Card Container */}
          <div style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 4px 12px rgba(72, 24, 47, 0.15), 0 2px 4px rgba(72, 24, 47, 0.08)'
          }}>
            {/* Filters */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '16px' }}>
              <input
                type="text"
                className="form-input"
                placeholder="Search by unit no .."
                value={searchQuery}
                onChange={handleSearchChange}
              />
              <select
                className="form-input"
                value={filters.buildingType || ''}
                onChange={(e) => handleFilterChange('buildingType', e.target.value)}
              >
                <option value="">Select Building Type</option>
                <option value="Condominium">Condominium</option>
                <option value="Townhouse">Townhouse</option>
                <option value="Detached">Detached</option>
                <option value="Semi-Detached">Semi-Detached</option>
              </select>
              <select
                className="form-input"
                value={filters.status || ''}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">All Status</option>
                <option value="AVAILABLE">Available</option>
                <option value="SOLD">Sold</option>
                <option value="RESERVED">Reserved</option>
                <option value="ON_HOLD">On Hold</option>
              </select>
              <select
                className="form-input"
                value={filters.constructionStatus || ''}
                onChange={(e) => handleFilterChange('constructionStatus', e.target.value)}
              >
                <option value="">All Construction Status</option>
                <option value="Under Construction">Under Construction</option>
                <option value="Released on Condition">Released on Condition</option>
                <option value="Released">Released</option>
              </select>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
              <button className="btn btn-primary btn-sm" onClick={() => fetchUnits(true)} disabled={dataLoading}>
                Search
              </button>
              <button className="btn btn-secondary btn-sm" onClick={handleClearFilters} disabled={dataLoading}>
                Clear
              </button>
              <button
                className="btn btn-dark btn-sm"
                onClick={handleChangeStatus}
                disabled={selectedUnits.size === 0 || dataLoading}
              >
                Change Status
              </button>
            </div>

            {loading ? (
              <div className="builders-loading">
                <div className="builders-loading-spinner" />
              </div>
            ) : units.length === 0 ? (
              <div className="builders-empty">
                <p className="builders-empty-title">No units found</p>
                <p className="builders-empty-text">
                  {filters.projectId ? 'No units available for the selected filters' : 'Please select a project to view units'}
                </p>
              </div>
            ) : (
              <>
                <div className="data-table-container" style={{ position: 'relative', marginBottom: '20px' }}>
                  {/* Loading Overlay */}
                  {dataLoading && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(255, 255, 255, 0.8)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 10
                    }}>
                      <div className="contacts-loading-spinner"></div>
                    </div>
                  )}

                  <table className="data-table">
                    <thead>
                      <tr>
                        <th style={{ width: '50px' }}>
                          <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={handleSelectAll}
                            disabled={units.length === 0 || dataLoading}
                          />
                        </th>
                        <th>Unit #</th>
                        <th>Under Construction</th>
                        <th>Released on Condition</th>
                        <th>Released</th>
                      </tr>
                    </thead>
                    <tbody style={{ opacity: dataLoading ? 0.5 : 1, transition: 'opacity 0.2s' }}>
                      {units.map((unit) => (
                        <tr key={unit._id} style={{ cursor: dataLoading ? 'wait' : 'default' }}>
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedUnits.has(unit._id)}
                              onChange={() => handleSelectUnit(unit._id)}
                              disabled={dataLoading}
                            />
                          </td>
                          <td className="data-table-primary-cell">
                            <strong>{unit.unitNo}</strong>
                          </td>
                          <td>{formatDate(unit.underConstructionDate)}</td>
                          <td>{formatDate(unit.releasedOnConditionDate)}</td>
                          <td>{formatDate(unit.releasedDate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination and Records Per Page */}
                <div className="contacts-pagination-controls">
                  {/* Records Per Page Selector */}
                  <div className="contacts-records-per-page">
                    <span>Show</span>
                    <select
                      value={pagination.limit}
                      onChange={(e) => handleLimitChange(Number(e.target.value))}
                      disabled={dataLoading}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <span>records per page (Total: {pagination.total})</span>
                  </div>

                  {/* Pagination */}
                  {pagination.pages > 1 && (
                    <div className="contacts-pagination-wrapper" style={{ pointerEvents: dataLoading ? 'none' : 'auto', opacity: dataLoading ? 0.6 : 1 }}>
                      <Pagination
                        current={pagination.page}
                        total={pagination.pages}
                        onPageChange={handlePageChange}
                        maxWidth={400}
                      />
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Bulk Update Modal */}
        {showBulkUpdateModal && (
          <ConstructionStatusBulkUpdateModal
            selectedCount={selectedUnits.size}
            selectedUnitNos={selectedUnitNos}
            onUpdate={handleBulkUpdate}
            onCancel={() => setShowBulkUpdateModal(false)}
          />
        )}
      </>
    </AppLayout>
  );
};

export default ConstructionStatus;
